import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  obtenerCarrerasActivas,
  obtenerEstudiantesDocsPendientesExcel,
  obtenerEstudiantesDocsPendientes,
} from "../../redux/solicitudesAdmSlice";
import { OrderTable } from "../../Components/OrderTable";
import Pagination from "./Paginacion";
import LoadingSpiner from "../../Components/LoadingSpiner";
import OperationStatus from "../../config/OperationStatus";
import { toast } from "react-toastify";
import { getAdminPendingPage } from "../../Api/content";
import { useLocale } from "../../i18n/localeContext";
import { Skeleton } from "antd";
import { _translateValue } from "../translation-gestor/translateValue";

const SCREEN_NAME = "PantallaListaEstudiantesDocumentosPendientes";

const getColumns = (adminPendingPage, translatedCarreras = {}) => {
  const table = adminPendingPage?.pending_table ?? [];
  return [
    { label: table[0]?.label || "Solicitud no.", accessor: "solicitudId", sortable: true },
    { label: table[1]?.label || "No. identificación", accessor: "dni", sortable: true },
    { label: table[2]?.label || "Nombre", accessor: "nombre", sortable: true },
    { label: table[3]?.label || "ID", accessor: "matricula", sortable: true },
    { 
      label: table[4]?.label || "Carrera", 
      accessor: "carrera", 
      sortable: true,
      render: (item) => (
        <td>{translatedCarreras[item.carrera] || item.carrera}</td>
      ),
    },
    { label: table[5]?.label || "Teléfono", accessor: "telefono", sortable: false },
    { label: table[6]?.label || "Correo", accessor: "correo" },
    { label: table[7]?.label || "Documento pendiente", accessor: "documentoPendiente" },
    { label: table[8]?.label || "Tipo prórroga", accessor: "tipoProrroga" },
    {
      label: table[9]?.label || "Fecha de vencimiento",
      accessor: "fechaVencimiento",
      sortable: true,
    },
  ];
};

export default function ListaEstudiantesDocumentosPendientes(props) {
  const [rowCount, setRowCount] = useState(15);
  const [currentPage, setCurrentPage] = useState(1);

  const { locale } = useLocale();
  const [adminPendingPage, setAdminPendingPage] = useState(null);
  const [isLoadingContent, setIsLoadingContent] = useState(true);
  const [translatedCarreras, setTranslatedCarreras] = useState({});
  const [isTranslating, setIsTranslating] = useState(false);

  const [carrerasActivas, setCarrerasActivas] = useState([]);
  const [filtroCarrera, setFiltroCarrera] = useState(0);

  const dispatch = useDispatch();
  const busqueda = useRef();

  const { estudiantesDocsPendientes, loadingEstudiantesDocsPendientes } =
    useSelector((state) => state.solicitudesAdm);

  const handleResetPagination = () => {
    setCurrentPage(1);
    setRowCount(15);
  };

  useEffect(() => {
    handleResetPagination();
  }, [filtroCarrera]);

    const cargarEstudiantesPorCaracter = () => {
      handleResetPagination();
      dispatch(
        obtenerEstudiantesDocsPendientes({
          carrera: filtroCarrera,
          busqueda: busqueda.current?.value || "",
          rowCount,
          page: currentPage,
        })
      );
    };

  const descargarArchivo = async () => {
    dispatch(
      obtenerEstudiantesDocsPendientesExcel({
        carrera: filtroCarrera,
        busqueda: busqueda.current?.value || "",
      })
    ).then(({ payload }) => {
      if (payload.operationStatus === OperationStatus.SUCCESS) {
        toast.success(payload.message);
      } else {
        toast.error(payload.message);
      }
    });
  };

  useEffect(() => {
    dispatch(obtenerCarrerasActivas()).then((res) => {
      setCarrerasActivas(res.payload);
    });
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      obtenerEstudiantesDocsPendientes({
        carrera: filtroCarrera,
        busqueda: busqueda.current?.value || "",
        rowCount,
        page: currentPage,
      })
    );
  }, [currentPage, dispatch, rowCount, filtroCarrera]);

  useEffect(() => {
    let cancel = false;
    setIsLoadingContent(true);
    getAdminPendingPage(locale)
      .then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          setAdminPendingPage(data);
          setIsLoadingContent(false);
        }
      })
      .catch(() => { 
        if (!cancel) {
          setAdminPendingPage(null);
          setIsLoadingContent(false);
        }
      });
    return () => { cancel = true };
  }, [locale])

  // Efecto para traducir el campo "Carrera" cuando lleguen los datos o cambie el locale
  useEffect(() => {
    const translateData = async () => {
      // Verificar si hay datos para traducir
      const hasEstudiantes = estudiantesDocsPendientes?.result && Array.isArray(estudiantesDocsPendientes.result) && estudiantesDocsPendientes.result.length > 0;

      // Si no hay datos, no hacer nada
      if (!hasEstudiantes) {
        setIsTranslating(false);
        return;
      }

      // Iniciar traducción
      setIsTranslating(true);

      const carreraTranslations = {};

      // Traducir solo el campo "Carrera"
      const translationPromises = estudiantesDocsPendientes.result.map(async (item) => {
        if (item.carrera) {
          const translated = await _translateValue(SCREEN_NAME, item.carrera, locale);
          carreraTranslations[item.carrera] = translated;
        }
      });
      await Promise.all(translationPromises);

      // Actualizar estados y finalizar traducción
      setTranslatedCarreras(carreraTranslations);
      setIsTranslating(false);
    };

    translateData();
  }, [estudiantesDocsPendientes?.result, locale]);

  if (isLoadingContent || isTranslating) {
    return (
      <main className="main-admin">
        <div className="main-admin-bar">
          <Skeleton.Input style={{ width: 400, height: 30 }} active />
          <div className="header-admin-bar-items ml-auto">
            <div className="select-filter-section-ml-auto">
              <Skeleton.Input style={{ width: 100, height: 40 }} active />
            </div>
            <Skeleton.Input style={{ width: 200, height: 40 }} active />
            <Skeleton.Button style={{ width: 40, height: 40 }} active />
          </div>
        </div>
        <div className="table-container relative-class">
          <Skeleton.Input style={{ width: '100%', height: 400, minWidth: '800px' }} active />
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
          <Skeleton.Input style={{ width: 300, height: 40 }} active />
        </div>
      </main>
    );
  }

  return (
    <main className="main-admin">
      <div className="main-admin-bar">
        <h2>{adminPendingPage?.title || "Estudiantes con documentos pendientes"}</h2>

        <div className="header-admin-bar-items ml-auto">
          <div className="select-filter-section-ml-auto">
            <label hidden>Carrera:</label>
            <select
              value={filtroCarrera}
              onChange={(e) => setFiltroCarrera(e.target.value)}
            >
              <option hidden>Carrera</option>
              <option value={0}>{adminPendingPage?.select_option || "Todas las carreras"}</option>
              {carrerasActivas?.map((a, i) => {
                return (
                  <option key={i} value={a.id}>
                    {a.carreraNombre}
                  </option>
                );
              })}
            </select>
          </div>
          <label hidden>Búsqueda</label>
          <input
            type="input"
            placeholder={adminPendingPage?.search_input || "Búsqueda"}
            ref={busqueda}
            className="form-control"
            onChange={cargarEstudiantesPorCaracter}
          />
          <button className="search-button" onClick={() => descargarArchivo()}>
            <i className="material-icons"></i>
          </button>
        </div>
      </div>

      <div className="table-container relative-class">
        {loadingEstudiantesDocsPendientes && (
          <div className="loading-background">
            <LoadingSpiner
              show
              styles={{
                top: "45%",
                left: "50%",
                width: "50px",
                height: "50px",
                position: "absolute",
                borderWidth: "10px",
                borderStyle: "solid",
                backgroundColor: "rgba(0, 0, 0, 0.3)",
                borderColor: "rgb(27 78 147) rgb(0 0 0) rgb(0 0 0)",
                borderImage: "initial",
              }}
            />
          </div>
        )}
        {estudiantesDocsPendientes.result?.length > 0 ? (
          <>
            <OrderTable
              columns={getColumns(adminPendingPage, translatedCarreras)}
              data={estudiantesDocsPendientes.result ?? []}
              clase="table-adm"
            />
          </>
        ) : (
          <div className="no-results-container">
            <img src={props.img} alt="Imagen sin resultados" />
            <h1>{props.noResultMessage}</h1>
          </div>
        )}
      </div>
      <Pagination
        rowCount={rowCount}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        onRowCountChange={setRowCount}
        totalPages={estudiantesDocsPendientes.pageCount ?? 1}
      />
    </main>
  );
}
